import React, { useEffect, useReducer } from "react"
import { Button, ButtonGroup, Col, Form, Row } from "react-bootstrap"
import { FormattedMessage, injectIntl } from "react-intl"
import { ConnectedProps, connect, useDispatch } from "react-redux"
import MoneyInputForm from "../../../common/form/moneyinput.component"
import Icon from "../../../common/icon/icon.component"
import Integrations from "../../../integration/Integrations"
import { INTEGRATION_KEY } from "../../../state/integration/Constants"
import ExternalLink from "../../../state/integration/ExternalLink"
import { fetchPriceLevels } from "../../../state/price-level/PriceLevelActions"
import { getPriceLevels } from "../../../state/price-level/PriceLevelSelector"
import ProductPrice from "../../../state/product-price/ProductPrice"
import { ProductPriceWithName } from "../../../state/product-price/types"
import { INTEGRATIONS_SUPPORTING_PRODUCT_PRICES } from "../../../utils/product-price/constant"
import { matchPriceLevelsWithProductPrice } from "../../../utils/product-price/utils"
import { addPrice, handlePriceChange, initialize, removePrice } from "./states/actions/productPriceActions"
import { InitialState, ProductPricesReducer } from "./states/reducers/productPriceReducer"

type OwnProps = {
    productPrices: ProductPrice[]
    productExternalLinks: ExternalLink[],
    isNewProduct: boolean,
    onChange: (updatedProductPrices) => void
}

const ProductPriceEditForm = ({ productPrices, priceLevels, productExternalLinks, isNewProduct, onChange }: Props) => {
    const [state, dispatch] = useReducer(ProductPricesReducer, InitialState)
    const reduxDispatch = useDispatch()

    const logoImagePath = Integrations.getIntegrationLogoImagePath(INTEGRATION_KEY.LIGHTSPEED_RETAIL, true)
    const { displayedProductPrices } = state
    const displayedProductPricesArr = displayedProductPrices ? Array.from(displayedProductPrices.entries()) : []
   
    useEffect(() => {
        reduxDispatch(fetchPriceLevels())
    }, [])
    
    useEffect(()=> {
        if (priceLevels && priceLevels?.length > 0 && productPrices && Array.from(displayedProductPrices.entries()).length == 0) {
            const displayedProductPrices = matchPriceLevelsWithProductPrice(productPrices, priceLevels)
            dispatch(initialize(displayedProductPrices))
        }
    }, [priceLevels])

    useEffect(() => {
        const modifiedMap = new Map()
        for (const [key, value] of displayedProductPrices.entries()) {
            if (value.recentlyModified) {
                modifiedMap.set(key, value)
            }
        }

        if (Array.from(modifiedMap.entries()).length > 0) {
            onChange(modifiedMap)
        }
    }, [displayedProductPrices])

    const getSupportedExternalLink = (): ExternalLink | null => {
        const link = productExternalLinks.find(link => INTEGRATIONS_SUPPORTING_PRODUCT_PRICES.includes(link.getIntegrationKey()))
        return link ? link : null
    }
    
    const hasExternalLink = (productPrice: ProductPriceWithName): boolean => {
        return !!productPrice.externalLinks
    }

    const hasLSExternalLink = (): boolean => {
        if (!getSupportedExternalLink() || productExternalLinks.length === 0) {
            return false
        }

        return displayedProductPricesArr
            .some(([,pp]) =>
                (pp.externalLinks?.some(
                    (el) => INTEGRATIONS_SUPPORTING_PRODUCT_PRICES.includes(el.getIntegrationKey())
                )) ?? false
            )
    }  
    
    const canBeDeleted = (): boolean => {
        return true
    }

    const canBeCreated = (productPrice: ProductPriceWithName) : boolean => {
        return (!productPrice.price && !hasExternalLink(productPrice)) || (hasExternalLink(productPrice) && productPrice.isArchived)
    }

    const hasPricesWithNoExternalLink = displayedProductPricesArr?.some(([, price]) => !price.externalLinks)
    const shouldDisplay = displayedProductPricesArr.length > 0
     && (hasPricesWithNoExternalLink || hasLSExternalLink())

    return (
        shouldDisplay &&
            <div className="col-12 ui-module">
                <h4 className={"custom-lead"}>
                    <FormattedMessage   
                        id="product.form.edit.product_price.title"
                        defaultMessage="Product Prices"
                    />
                </h4>
                {displayedProductPricesArr
                    .map(([priceLevelId, productPrice]) => (
                        <Row key={priceLevelId}>
                            <Col sm="4">
                                <Form.Label  className="mt-2">{productPrice.name}</Form.Label>
                            </Col>
                            {canBeCreated(productPrice) ?
                                <Col sm="4">
                                    <ButtonGroup aria-label="Basic example" >
                                        <Button className="mb-2"
                                            onClick={() => dispatch(addPrice(priceLevelId))} 
                                            variant="primary"
                                        >
                                            <FormattedMessage
                                                id="product.product_price_edit_form.add price level"
                                                defaultMessage="Add Product Price"
                                            />
                                        </Button>
                                    </ButtonGroup>
                                </Col>
                                :
                                <>
                                    <Col sm="4">
                                        <MoneyInputForm
                                            className="mb-2"
                                            name="ProductPrice"
                                            value={productPrice.price}
                                            onChange={(val) => dispatch(handlePriceChange(priceLevelId, val))}
                                        />
                                    </Col>
                                    {canBeDeleted() && 
                                <Col sm="2">
                                    <Button variant="danger" className="mb-2" onClick={() => dispatch(removePrice(priceLevelId))}>
                                        <Icon value={"trash"}/>
                                    </Button>
                                </Col>
                                    }
                                </>
                            }
                        </Row>
                    ))}
                {!isNewProduct && hasLSExternalLink() &&
            <div className="d-flex align-items-center mt-5">
                <Form.Text className="mt-2 mr-2" id="lsRetailText" muted>
                    <FormattedMessage   
                        id="product.form.edit.product_prices_integration_label"
                        defaultMessage="Certain product prices have to be edited in {integration}"
                        values={{
                            integration: Integrations.getIntegrationNameFromKey(INTEGRATION_KEY.LIGHTSPEED_RETAIL)
                        }}
                    />
                </Form.Text>
                <a key={getSupportedExternalLink()?.getId()} 
                    href={Integrations.getProductExternalUrl(getSupportedExternalLink()?.getDto())} 
                    className={"btn btn-outline-secondary ml-2"} 
                    target={"_blank"} 
                    rel="noreferrer"
                >
                    <img src={logoImagePath} className={"mr-1"}
                        style={{ width: "1rem" }} 
                    />
                    <FormattedMessage
                        id="customer.view.open_in_external_integration"
                        defaultMessage="Open in {integrationName}"
                        values={{
                            integrationName: Integrations.getIntegrationNameFromKey(INTEGRATION_KEY.LIGHTSPEED_RETAIL)
                        }}
                    />
                </a>    
            </div>            
                }
            </div>
    )
}

const mapState = (state, props: OwnProps) => {
    return {
        ...props,
        priceLevels: getPriceLevels(state)
    }
}
const mapDispatch = {}
const connector = connect(mapState, mapDispatch)
type Props = ConnectedProps<typeof connector>
export default connector(injectIntl(ProductPriceEditForm))


// WEBPACK FOOTER //
// ./src/product/form/components/ProductPriceEditForm.tsx